/**
 * T-05 — generic queue-scanning/backoff engine. Owns the four wake sources,
 * exponential backoff, and the head-of-line rule (§1.5). Does NOT know how
 * to actually deliver a group; it's injected a `DispatchSender` (T-06's
 * `RetryUnit` implements it) so this file stays testable without any real
 * network/transport concerns.
 */

import { applyAwaitConfirm } from "../confirm/pending-confirm";
import { markSessionStale, isSessionStale } from "../auth/session-stale";
import { getOutboxByState, getOutboxRecord } from "./db";
import { markDelivered, markQueued, markRejected, markSending } from "./state-machine";
import type { OutboxRecord, ReplyEnvelope } from "./types";

export interface DispatchSender {
  sendGroup(record: OutboxRecord, signal?: AbortSignal): Promise<ReplyEnvelope>;
}

const TIMER_FLOOR_MS = 30_000;
const BACKOFF_BASE_MS = 30_000;
const BACKOFF_FACTOR = 2;
const BACKOFF_CAP_MS = 5 * 60_000;
const MAX_CONCURRENT_GROUPS = 2;

/** Exponential backoff with jitter, capped at 5 minutes (§1.5). `jitterFn`
 * is injectable so tests can pin the jitter band deterministically. */
export function computeBackoff(retryCount: number, jitterFn: () => number = Math.random): number {
  const raw = Math.min(BACKOFF_BASE_MS * Math.pow(BACKOFF_FACTOR, retryCount), BACKOFF_CAP_MS);
  const jitter = 0.5 + jitterFn(); // 0.5x-1.5x band, always positive
  return Math.round(raw * jitter);
}

export function isEligible(record: OutboxRecord, now: number): boolean {
  return record.state === "queued" && record.retryAt <= now;
}

/** Oldest-eligible-first, capped at `maxConcurrent` groups in flight
 * (§1.5). A permanently-backed-off entry simply isn't eligible yet — it
 * never blocks the batch from picking up others behind it. */
export function selectDispatchBatch(
  records: OutboxRecord[],
  now: number,
  maxConcurrent = MAX_CONCURRENT_GROUPS,
): OutboxRecord[] {
  return records
    .filter((r) => isEligible(r, now))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .slice(0, maxConcurrent);
}

export class Dispatcher {
  private timer: ReturnType<typeof setInterval> | null = null;
  private inFlight = new Set<string>();
  private abortController = new AbortController();

  constructor(
    private sender: DispatchSender,
    private now: () => number = Date.now,
    private ownerPrincipal?: string,
  ) {}

  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => void this.runOnce(), TIMER_FLOOR_MS);
    if (typeof window !== "undefined") {
      window.addEventListener("online", this.wake);
      document.addEventListener("visibilitychange", this.onVisibility);
    }
    // SW Background Sync (Android): enhancement-only, never load-bearing
    // (§1.5). The service worker (T-01) posts a message to wake this
    // dispatcher when a sync event fires; absence of that channel is a
    // silent no-op — the timer floor + foreground wake sources are the
    // guaranteed path.
  }

  stop(): void {
    this.abortController.abort();
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.wake);
      document.removeEventListener("visibilitychange", this.onVisibility);
    }
  }

  private wake = (): void => void this.runOnce();

  private onVisibility = (): void => {
    if (document.visibilityState === "visible") void this.runOnce();
  };

  async runOnce(): Promise<void> {
    // WI-B-05: while the session is stale, hold — do not scan/send. Every send
    // would just 401 again and re-pin the banner; captures stay safely queued
    // and new captures keep recording. Sign-back-in clears the flag, and the
    // next wake (timer/online/visibility/explicit) drains the held queue.
    if (isSessionStale()) return;
    const queued = await getOutboxByState("queued");
    const eligible = queued.filter(
      (r) => !this.inFlight.has(r.capture_id) && (!this.ownerPrincipal || r.owner_principal === this.ownerPrincipal),
    );
    const batch = selectDispatchBatch(eligible, this.now());
    await Promise.allSettled(batch.map((r) => this.dispatchOne(r)));
  }

  private async dispatchOne(record: OutboxRecord): Promise<void> {
    this.inFlight.add(record.capture_id);
    try {
      await markSending(record.capture_id);
      // Re-fetch: the sender persists the record it's handed (e.g. resolved
      // media_refs/parts_delivered progress), and `record` here is the
      // pre-dispatch snapshot from the batch scan — still showing
      // state:"queued". Passing that stale object through would clobber
      // the markSending write the moment the sender's own durability write
      // lands, breaking the later sending->delivered transition.
      const fresh = (await getOutboxRecord(record.capture_id))!;
      const reply = await this.sender.sendGroup(fresh, this.abortController.signal);
      if (this.abortController.signal.aborted) return;
      if (reply.status === "owner-changed") {
        markSessionStale();
        this.stop();
        return;
      } else if (reply.status === "accepted") {
        await markDelivered(record.capture_id);
        // Skill #2's dedup ASK rides back on the reply to the capture that
        // triggered it. Until this read it, the ask was produced, staged, and
        // discarded — and the fully-built confirm card waited for a message
        // that never came.
        //
        // AFTER markDelivered, never before: `applyAwaitConfirm` requires a
        // DELIVERED record, which is also the honest order — the capture landed
        // whether or not a question came back with it.
        const ask = reply.payload?.await_confirm;
        if (ask) {
          try {
            await applyAwaitConfirm(record.capture_id, ask);
          } catch {
            // A failure to STAMP the question must never undo the delivery or
            // re-queue the capture — that would re-send a capture the server
            // already has. The worst case here is a duplicate the human is
            // never asked about, which is exactly where we were before.
          }
        }
      } else if (reply.status === "permanent-reject") {
        await markRejected(record.capture_id, reply.error);
      } else if (reply.status === "auth-stale") {
        // WI-B-05: the SESSION is stale (bare 401), the capture is fine. Hold it
        // QUEUED without charging a retry/backoff — the queue is healthy; only
        // the credential expired. Raise the session-stale signal (pins A7) and
        // stop sending; the next drain happens after sign-back-in clears it.
        // retryAt stays put so the record is immediately eligible on resume.
        markSessionStale();
        await markQueued(record.capture_id, record.retryAt, record.retryCount);
      } else {
        const nextCount = record.retryCount + 1;
        await markQueued(record.capture_id, this.now() + computeBackoff(nextCount), nextCount);
      }
    } catch {
      if (this.abortController.signal.aborted) return;
      // Network throw / offline: treat identically to transient-fail-retry.
      const nextCount = record.retryCount + 1;
      await markQueued(record.capture_id, this.now() + computeBackoff(nextCount), nextCount);
    } finally {
      this.inFlight.delete(record.capture_id);
    }
  }
}
